// role-based-dashboards (review FIX 3) — shared TanStack Query `retry`
// predicate for queries that can legitimately 403 (or 4xx more generally)
// as an EXPECTED outcome rather than a transient failure.
//
// Problem: TanStack Query's default `retry: 3` (with exponential backoff)
// treats every rejection the same way, including a 403 that means "this
// plan does not include this feature" — an outcome that will NEVER
// succeed on retry. Left at the default, a gated widget fires 4 requests
// (1 + 3 retries) and shows a loading skeleton for several seconds before
// finally surfacing the "upgrade" placeholder, even though the very first
// response already told us definitively what to render.
//
// Fix: any 4xx `ApiError` (400-499) is treated as terminal — don't retry.
// Anything else (network errors, 5xx, non-ApiError failures) keeps the
// default retry behavior (up to 3 attempts), since those genuinely can be
// transient.
import { ApiError } from '@jq4race/api-client';

const DEFAULT_MAX_RETRIES = 3;

// `error: Error` (not `unknown`) is deliberate: TanStack Query's `useQuery`
// defaults its `TError` generic to `Error`, and it infers that generic from
// the shape of the `retry` option passed in. Typing this parameter as
// `unknown` would widen every consuming query's inferred error type to
// `unknown` too, breaking existing `error: Error | null` prop types
// downstream (e.g. `<SectionErrorState error={error} />`).
export function retryOnNonClientError(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status !== undefined) {
    if (error.status >= 400 && error.status < 500) {
      return false;
    }
  }
  return failureCount < DEFAULT_MAX_RETRIES;
}
